import { Avatar, Box, Grid, LinearProgress, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { cloneElement } from 'react';
import VNewsArticleLastDashboard from '../../../news/views/VNewsArticleLastDashboard';
import VMiscMarketingDashboard from '../../../misc/views/VMiscMarketingDashboard';
import CAppPaper from '../../../../app/components/CAppPaper';
import useInvestExpertises from '../../hooks/expertise/useInvestExpertises';
import InvestorRoutes from '../../InvestorRoutes';
import HAppRoutes from '../../../../app/features/routes/helpers/HAppRoutes';
import { useNavigate } from 'react-router';
import AppFormatAmount from '../../../../app/features/format/AppFormatAmount';
import { useTranslation } from 'react-i18next';
import AppFormatX from '../../../../app/features/format/AppFormatX';
import AppFormatPercent from '../../../../app/features/format/AppFormatPercent';
import CAppInfoTooltip from '../../../../app/components/CAppInfoTooltip';
import CInvestDashboardGraphMultiExpertises from '../../components/dashboard/CInvestDashboardGraphMultiExpertises';
import { TInvestExpertiseTag } from '../../types/expertise/TInvestExpertiseTag';
import _ from 'lodash';
import { useAppNavigationContext } from '../../../../app/features/navigation/providers/AppNavigationProvider';
import langInvestDashboardMultiExpertises from '../../langs/langInvestDashboardMultiExpertises';
import useInvestDashboardKpis from '../../hooks/dashboard/useInvestDashboardKpis';
import AppLoaderContent from '../../../../app/features/loader/content/AppLoaderContent';

const MULTI_EXPERTISE_LIST: Exclude<TInvestExpertiseTag, 'real_estate' | 'venture_capital' | 'private_equity'>[] = [
	'private_credit',
	'listed_assets',
];

const MULTI_EXPERTISE_ITEM_ORGANISATION: {
	[K in (typeof MULTI_EXPERTISE_LIST)[number]]: {
		[M in
			| 'committedCapital'
			| 'outstandingCapital'
			| 'grossIrr'
			| 'moic'
			| 'calledCapital'
			| 'callableCapital']: boolean;
	};
} = {
	private_credit: {
		calledCapital: true,
		callableCapital: true,
		committedCapital: true,
		outstandingCapital: false,
		grossIrr: true,
		moic: false,
	},
	listed_assets: {
		calledCapital: false,
		callableCapital: false,
		committedCapital: false,
		outstandingCapital: true,
		grossIrr: false,
		moic: false,
	},
};

const VInvestDashboardMultiExpertises = () => {
	const investExpertises = useInvestExpertises();

	const navigationContext = useAppNavigationContext();
	const selectedCurrency = navigationContext.currentCurrency;
	const { t } = useTranslation();
	const { isFetching, fundTypes } = useInvestDashboardKpis();
	if (isFetching || !fundTypes) return <AppLoaderContent />;
	const sortedFundTypes = _.sortBy(fundTypes, (x) => -x.committedCapital);
	return (
		<Grid container spacing={3} alignItems="stretch">
			<Grid item xs={12} sm={6}>
				<CAppPaper variant="outlined" color="primary">
					<Typography
						variant="h3"
						component="h3"
						color="inherit"
						style={{
							margin: '0px',
							fontFamily: "'Poppins'",
							fontStyle: 'normal',
							fontWeight: '600',
							fontSize: '1.25rem',
							lineHeight: '1.5',
							textAlign: 'center',
						}}
					>
						{t(langInvestDashboardMultiExpertises.graph_title_your_investment_in.tag)} {selectedCurrency}
						<CAppInfoTooltip
							color="inherit"
							title={t(langInvestDashboardMultiExpertises.graph_tooltip.tag)}
						/>
					</Typography>
					<Box sx={{ marginBottom: '20px', paddingTop: '30px' }}>
						<CInvestDashboardGraphMultiExpertises
							graphKpis={sortedFundTypes}
							selectedCurrency={selectedCurrency}
						/>
					</Box>
					{sortedFundTypes
						?.filter((x) => x.currency === selectedCurrency)
						.map((graphKpi) => {
							const percent = _.round(
								(graphKpi.committedCapital /
									_.sumBy(
										sortedFundTypes.filter((x) => x.currency === selectedCurrency),
										'committedCapital',
									)) *
									100,
								2,
							);
							return (
								<Stack key={graphKpi.tag} sx={{ marginBottom: '20px' }}>
									<Box>{investExpertises.find((x) => x.tag === graphKpi.tag)?.name}</Box>
									<Box sx={{ display: 'flex', alignItems: 'center' }}>
										<Box sx={{ width: '100%', mr: 2 }}>
											<LinearProgress
												value={percent}
												variant="determinate"
												sx={{
													'& .MuiLinearProgress-bar': {
														backgroundColor: investExpertises.find(
															(x) => x.tag === graphKpi.tag,
														)?.sliceColor.dark,
													},
												}}
											/>
										</Box>
										<Box sx={{ minWidth: 35 }}>
											<Typography variant="body2" color="default">
												{percent}%
											</Typography>
										</Box>
									</Box>
								</Stack>
							);
						})}
				</CAppPaper>
			</Grid>
			<Grid container item spacing={3} alignItems="stretch" xs={12} sm={6}>
				{sortedFundTypes
					.filter((fundType): fundType is typeof fundType & { tag: MultiExpertiseTag } =>
						(MULTI_EXPERTISE_LIST as TInvestExpertiseTag[]).includes(fundType.tag),
					)
					.map((fundType) => (
						<Grid item key={fundType.tag} xs={12}>
							<ExpertiseItem
								expertiseTag={fundType.tag}
								calledCapital={fundType.calledCapital}
								callableCapital={fundType.callableCapital}
								fundsCount={fundType.fundsCount}
								committedCapital={fundType.committedCapital}
								currency={fundType.currency as 'EUR' | 'USD' | undefined}
								outstandingCapital={fundType.outstandingCapital}
								grossIrr={fundType.grossIrr}
								moic={fundType.moic}
								enabled
							/>
						</Grid>
					))}
			</Grid>
			<Grid item xs={12} sm={8}>
				<VMiscMarketingDashboard />
			</Grid>
			<Grid item xs={12} sm={4}>
				<VNewsArticleLastDashboard size="small" />
			</Grid>
		</Grid>
	);
};
// Restricted to expertise tags that have an entry in `MULTI_EXPERTISE_ITEM_ORGANISATION`.
// Without this, lookups like `MULTI_EXPERTISE_ITEM_ORGANISATION[expertiseTag]` would
// crash at runtime for `'venture_capital'` / `'private_equity'` / `'real_estate'`.
type MultiExpertiseTag = (typeof MULTI_EXPERTISE_LIST)[number];

type ExpertiseItemProps = {
	expertiseTag: MultiExpertiseTag;
	fundsCount: number;
	committedCapital?: number;
	currency?: 'EUR' | 'USD';
	outstandingCapital?: number;
	calledCapital?: number;
	callableCapital?: number;
	grossIrr?: number;
	moic?: number;
	enabled?: boolean;
};
const ExpertiseItem = (props: ExpertiseItemProps) => {
	const {
		expertiseTag,
		fundsCount,
		calledCapital,
		callableCapital,
		committedCapital,
		currency,
		outstandingCapital,
		grossIrr,
		moic,
		enabled,
	} = props;

	const investExpertises = useInvestExpertises();
	const investExpertise = investExpertises?.find((x) => x.tag === expertiseTag);

	const theme = useTheme();
	const navigate = useNavigate();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const { t } = useTranslation();

	const handleExplore = () => {
		navigate(HAppRoutes.getPath(InvestorRoutes.fund_type.tag, { investExpertiseTag: expertiseTag }));
	};

	return (
		<CAppPaper
			className="expertise-item"
			color="default"
			variant="outlined"
			style={{
				cursor: enabled ? 'pointer' : undefined,
				padding: isMobile ? '15px' : '20px',
			}}
			onClick={() => enabled && handleExplore()}
		>
			<Stack direction="row" alignItems="center" justifyContent="left">
				<Avatar
					sx={{
						width: isMobile ? '47px' : '60px',
						height: isMobile ? '47px' : '60px',

						backgroundColor: 'rgba(42, 189, 187, 0.15)',
						marginRight: '12px',
					}}
				>
					{investExpertise?.icon &&
						cloneElement(investExpertise.icon as React.ReactElement<{ style?: React.CSSProperties }>, {
							style: {
								width: '34px',
								height: '34px',
								color:
									theme.palette.mode === 'dark'
										? theme.palette.secondary.main
										: theme.palette.primary.main,
							},
						})}
				</Avatar>
				<Typography
					variant="h3"
					component="h3"
					style={{
						margin: '0px',
						fontFamily: "'Poppins'",
						fontStyle: 'normal',
						fontWeight: '600',
						fontSize: '1.25rem',
						lineHeight: '1.5',
					}}
				>
					{investExpertise && t(investExpertise.name)}
				</Typography>
			</Stack>
			<Box sx={{ padding: '10px 0px', lineHeight: '1.2' }}>
				{MULTI_EXPERTISE_ITEM_ORGANISATION[expertiseTag].committedCapital && committedCapital !== null ? (
					<Stack direction="row" alignItems="center" justifyContent="space-between">
						<Box sx={{ fontWeight: 500, fontSize: '1rem', lineHeight: '1.5rem' }}>
							{t(langInvestDashboardMultiExpertises.committed_capital.tag)}
						</Box>
						<Typography
							color="primary"
							style={{
								margin: '0px',
								fontFamily: "'Poppins'",
								fontStyle: 'normal',
								fontWeight: '600',
								fontSize: '1.5rem',
								lineHeight: '1.85',
							}}
						>
							<AppFormatAmount value={committedCapital} addSpace currency={currency} />
						</Typography>
					</Stack>
				) : (
					<></>
				)}
				{MULTI_EXPERTISE_ITEM_ORGANISATION[expertiseTag].calledCapital && calledCapital !== null ? (
					<Stack direction="row" alignItems="center" justifyContent="space-between">
						<Box sx={{ fontWeight: 500, fontSize: '1rem', lineHeight: '1.5rem' }}>
							{t(langInvestDashboardMultiExpertises.called_capital.tag)}
						</Box>
						<Typography
							color="primary"
							style={{
								margin: '0px',
								fontFamily: "'Poppins'",
								fontStyle: 'normal',
								fontWeight: '600',
								fontSize: '1.5rem',
								lineHeight: '1.85',
							}}
						>
							<AppFormatAmount value={calledCapital} addSpace currency={currency} />
						</Typography>
					</Stack>
				) : (
					<></>
				)}
				{MULTI_EXPERTISE_ITEM_ORGANISATION[expertiseTag].callableCapital && callableCapital !== null ? (
					<Stack direction="row" alignItems="center" justifyContent="space-between">
						<Box sx={{ fontWeight: 500, fontSize: '1rem', lineHeight: '1.5rem' }}>
							{t(langInvestDashboardMultiExpertises.callable_capital.tag)}
						</Box>
						<Typography
							color="primary"
							style={{
								margin: '0px',
								fontFamily: "'Poppins'",
								fontStyle: 'normal',
								fontWeight: '600',
								fontSize: '1.5rem',
								lineHeight: '1.85',
							}}
						>
							<AppFormatAmount value={callableCapital} addSpace currency={currency} />
						</Typography>
					</Stack>
				) : (
					<></>
				)}

				{MULTI_EXPERTISE_ITEM_ORGANISATION[expertiseTag].outstandingCapital && outstandingCapital !== null ? (
					<Stack direction="row" alignItems="center" justifyContent="space-between">
						<Box sx={{ fontWeight: 500, fontSize: '1rem', lineHeight: '1.5rem' }}>
							{t(langInvestDashboardMultiExpertises.encours.tag)}
						</Box>
						<Typography
							color="primary"
							style={{
								margin: '0px',
								fontFamily: "'Poppins'",
								fontStyle: 'normal',
								fontWeight: '600',
								fontSize: '1.5rem',
								lineHeight: '1.85',
							}}
						>
							<AppFormatAmount value={outstandingCapital} addSpace currency={currency} />
						</Typography>
					</Stack>
				) : (
					<></>
				)}

				<Stack direction="row" alignItems="center" justifyContent="space-between">
					<Box sx={{ fontWeight: 500, fontSize: '1rem', lineHeight: '1.5rem' }}>
						{t(langInvestDashboardMultiExpertises.nb_funds.tag)}
					</Box>
					<Typography
						color="primary"
						style={{
							margin: '0px',
							fontFamily: "'Poppins'",
							fontStyle: 'normal',
							fontWeight: '600',
							fontSize: '1.5rem',
							lineHeight: '1.85',
						}}
					>
						{fundsCount}
					</Typography>
				</Stack>
				{MULTI_EXPERTISE_ITEM_ORGANISATION[expertiseTag].grossIrr && grossIrr !== null && (
					<Stack direction="row" alignItems="center" justifyContent="space-between">
						<Box sx={{ fontWeight: 500, fontSize: '1rem', lineHeight: '1.5rem' }}>
							{t(langInvestDashboardMultiExpertises.gross_net_irr.tag)}
						</Box>
						<Typography
							color="primary"
							style={{
								margin: '0px',
								fontFamily: "'Poppins'",
								fontStyle: 'normal',
								fontWeight: '600',
								fontSize: '1.5rem',
								lineHeight: '1.85',
							}}
						>
							<AppFormatPercent value={grossIrr} />
						</Typography>
					</Stack>
				)}
				{MULTI_EXPERTISE_ITEM_ORGANISATION[expertiseTag].moic && moic !== null && (
					<Stack direction="row" alignItems="center" justifyContent="space-between">
						<Box sx={{ fontWeight: 500, fontSize: '1rem', lineHeight: '1.5rem' }}>
							{t(langInvestDashboardMultiExpertises.global_multiple.tag)}
						</Box>
						<Typography
							color="primary"
							style={{
								margin: '0px',
								fontFamily: "'Poppins'",
								fontStyle: 'normal',
								fontWeight: '600',
								fontSize: '1.5rem',
								lineHeight: '1.85',
							}}
						>
							<AppFormatX value={moic} />
						</Typography>
					</Stack>
				)}
			</Box>
		</CAppPaper>
	);
};

export default VInvestDashboardMultiExpertises;
